import React, { useState, useEffect } from 'react';
import { localePath } from '../config/region';
import { useParams, useLocation } from 'react-router-dom';
import { API_URLS } from '../config/api';
import { apiGet, apiPost, apiPut, apiDelete } from '../utils/apiUtils';
import { reinitializeSceneIds } from '../utils/novelUtils';
import ParticleBackground from './ParticleBackground';
import './InteractiveSceneDetail.css';
import InteractiveSceneInfo from './interactive_scene/InteractiveSceneInfo';
import InteractiveSceneCharacters from './interactive_scene/InteractiveSceneCharacters';
import InteractiveSceneBackground from './interactive_scene/InteractiveSceneBackground';
import InteractiveSceneMusic from './interactive_scene/InteractiveSceneMusic';
import InteractiveScenePrompts from './interactive_scene/InteractiveScenePrompts';
import InteractiveSceneModifyingStates from './interactive_scene/InteractiveSceneModifyingStates';
import InteractiveSceneEndings from './interactive_scene/InteractiveSceneEndings';
import InteractiveSceneTestCanvas from './interactive_scene/InteractiveSceneTestCanvas';
import InteractiveSceneStartMessage from './interactive_scene/InteractiveSceneStartMessage';
import CustomSelect from './CustomSelect';

const CATEGORIES = [
  { id: 'info', label: 'Информация', icon: '📖' },
  { id: 'characters', label: 'Персонажи', icon: '🎭' },
  { id: 'background', label: 'Фон', icon: '🖼️' },
  { id: 'music', label: 'Музыка', icon: '🎶' },
  { id: 'scenePrompts', label: 'Подсказки', icon: '✍️' },
  { id: 'modifyingStates', label: 'Состояния', icon: '⚙️' },
  { id: 'endings', label: 'Концовки', icon: '🌙' },
  { id: 'startMessage', label: 'Сообщение', icon: '💬' },
  { id: 'testCanvas', label: 'Тест', icon: '🧪' }
];

const getInitialTab = () => {
  const tab = new URLSearchParams(window.location.search).get('tab');
  return CATEGORIES.some(c => c.id === tab) ? tab : 'info';
};

const InteractiveSceneDetail = () => {
  const { novelId, sceneId } = useParams();
  const location = useLocation();

  // Detect production mode from URL
  const isProduction = window.location.pathname.includes('production_change');
  const [scene, setScene] = useState(null);
  const [novel, setNovel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(getInitialTab);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [copyName, setCopyName] = useState('');
  const [copyLoading, setCopyLoading] = useState(false);

  // Fetch scene and novel data
  const fetchSceneData = async () => {
    try {
      setLoading(true);
      setError(null);
      const apiEndpoint = isProduction ? API_URLS.PRODUCTION_NOVEL_BY_ID(novelId) : `${API_URLS.BUILDING_NOVELS}/${novelId}`;
      const response = await apiGet(apiEndpoint);
      // Handle different response structures for production vs building
      const novelData = isProduction ?
        (response.data.full_novel ? {
          ...response.data.full_novel,
          id: response.data.full_novel.id || response.data.full_novel._id
        } : response.data) :
        response.data;
      setNovel(novelData);

      // Find the scene in the novel's interactive scenes
      const foundScene = novelData.interactive_scenes?.find(s => s.id === sceneId);
      if (foundScene) {
        setScene(foundScene);
      } else {
        setError('Сцена не найдена');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Initialize data on component mount
  useEffect(() => {
    fetchSceneData();
  }, [novelId, sceneId]);

  // Sync active tab with the ?tab= query param (e.g. when navigating to a next scene)
  useEffect(() => {
    const tab = new URLSearchParams(location.search).get('tab');
    if (tab && CATEGORIES.some(c => c.id === tab)) setSelectedCategory(tab);
  }, [location.search, sceneId]);

  const backHref = localePath(`/${isProduction ? 'production_change' : 'building'}/${novelId}`) + '?category=interactiveScenes';

  const openCopyModal = () => {
    setCopyName(`Копия: ${scene?.name || ''}`);
    setShowCopyModal(true);
  };

  const handleCopyConfirm = async () => {
    if (!copyName.trim() || !scene || copyLoading) return;

    try {
      setCopyLoading(true);
      setError(null);

      const createEndpoint = isProduction
        ? API_URLS.PRODUCTION_INTERACTIVE_SCENES(novelId)
        : API_URLS.BUILDING_INTERACTIVE_SCENES(novelId);

      const createResponse = await apiPost(createEndpoint, { name: copyName.trim(), tag: scene.tag });
      // Use the id the backend reports for the scene it just created. Diffing the
      // returned scene list can pick the wrong scene (and then overwrite it) when
      // the local list is stale; only fall back to that if the header is missing.
      const existingIds = new Set((novel?.interactive_scenes || []).map(s => s.id));
      const newSceneId = createResponse.headers?.['x-new-scene-id']
        || createResponse.data.interactive_scenes?.find(s => !existingIds.has(s.id))?.id;
      if (!newSceneId) throw new Error('Не удалось найти созданную сцену');

      const updateEndpoint = isProduction
        ? API_URLS.PRODUCTION_INTERACTIVE_SCENE(novelId, newSceneId)
        : API_URLS.BUILDING_INTERACTIVE_SCENE(novelId, newSceneId);

      const sceneCopy = reinitializeSceneIds(scene);
      // Offset the copy to the right of the original so it doesn't stack on the
      // same node in the graph (overlapping nodes make links/clicks ambiguous).
      // Only when the source has real stored coordinates, though: falling back to
      // 0 put every copy of a coordinate-less scene at exactly (250, 0), piling
      // them into one opaque white blob. null lets the graph auto-place the copy.
      const hasCoords = typeof scene.x === 'number' && typeof scene.y === 'number';
      sceneCopy.x = hasCoords ? scene.x + 250 : null;
      sceneCopy.y = hasCoords ? scene.y : null;
      await apiPut(updateEndpoint, { ...sceneCopy, id: newSceneId, name: copyName.trim() });

      const prefix = isProduction ? 'production_change' : 'building';
      window.location.href = localePath(`/${prefix}/${novelId}/interactive_scene/${newSceneId}`);

    } catch (err) {
      setError(err.message || 'Не удалось скопировать сцену');
      setCopyLoading(false);
      setShowCopyModal(false);
    }
  };

  // Handle delete scene
  const handleDeleteScene = async () => {
    if (window.confirm('Вы уверены, что хотите удалить эту сцену?')) {
      try {
        setLoading(true);
        const deleteEndpoint = isProduction ?
          API_URLS.PRODUCTION_INTERACTIVE_SCENE(novelId, sceneId) :
          `${API_URLS.BUILDING_NOVELS}/${novelId}/interactive_scenes/${sceneId}`;
        await apiDelete(deleteEndpoint);
        window.location.href = backHref;
      } catch (err) {
        setError(err.message || 'Не удалось удалить сцену');
      } finally {
        setLoading(false);
      }
    }
  };

  // Common props for all components
  const commonProps = {
    novelId,
    sceneId,
    scene,
    setScene,
    novel,
    isProduction,
    setNovel,
    loading,
    setLoading,
    error,
    setError
  };

  const categoryComponents = {
    info: <InteractiveSceneInfo {...commonProps} />,
    characters: <InteractiveSceneCharacters {...commonProps} />,
    background: <InteractiveSceneBackground {...commonProps} />,
    music: <InteractiveSceneMusic {...commonProps} />,
    scenePrompts: <InteractiveScenePrompts {...commonProps} />,
    modifyingStates: <InteractiveSceneModifyingStates {...commonProps} />,
    endings: <InteractiveSceneEndings {...commonProps} />,
    startMessage: <InteractiveSceneStartMessage {...commonProps} />,
    testCanvas: <InteractiveSceneTestCanvas {...commonProps} />,
  };

  if (loading && selectedCategory !== 'testCanvas') {
    return (
      <div className="isd-wrap">
        <ParticleBackground />
        <div className="isd-app">
          <div className="isd-loading">
            <div className="isd-spinner"></div>
            <p>Загрузка...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="isd-wrap">
      <ParticleBackground />
      <div className="isd-app">
        <div className="isd-header">
          <a href={backHref} className="isd-back">
            ← Назад к интерактивным сценам
          </a>
          <h1 className="isd-title">{scene?.name || 'Детали интерактивной сцены'}</h1>
          <div className="isd-header-actions">
            <button
              className="isd-btn-secondary"
              onClick={openCopyModal}
              disabled={loading}
            >
              Копировать сцену
            </button>
            <button
              className="isd-btn-danger"
              onClick={handleDeleteScene}
              disabled={loading}
            >
              Удалить сцену
            </button>
          </div>
        </div>

        {isProduction && (
          <div className="isd-prod-warning">
            ⚠️ Это продакшн-новелла, будьте осторожны ⚠️
          </div>
        )}

        {error && <div className="isd-error">{error}</div>}

        <CustomSelect
          className="isd-cat-select"
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
        >
          {CATEGORIES.map((cat) => (
            <option key={cat.id} value={cat.id}>{cat.label}</option>
          ))}
        </CustomSelect>

        <div className="isd-body">
          <div className="isd-sidebar">
            {CATEGORIES.map((category) => (
              <button
                key={category.id}
                className={`isd-sidebar-btn${selectedCategory === category.id ? ' active' : ''}`}
                onClick={() => setSelectedCategory(category.id)}
              >
                <span className="category-icon">{category.icon}</span>
                <span className="category-label">{category.label}</span>
              </button>
            ))}
          </div>

          <div className="isd-main">
            {categoryComponents[selectedCategory]}
          </div>
        </div>
      </div>

      {showCopyModal && (
        <div className="isd-overlay">
          <div className="isd-modal">
            <div className="isd-modal-head">
              <h3 className="isd-modal-title">Копировать сцену</h3>
              <button className="isd-modal-close" onClick={() => setShowCopyModal(false)}>×</button>
            </div>
            <div className="isd-modal-body">
              <div className="isd-form-group">
                <label className="isd-form-label">Название копии</label>
                <input
                  type="text"
                  value={copyName}
                  onChange={(e) => setCopyName(e.target.value)}
                  className="isd-input"
                  disabled={copyLoading}
                  autoFocus
                  onKeyDown={(e) => e.key === 'Enter' && handleCopyConfirm()}
                />
              </div>
            </div>
            <div className="isd-modal-footer">
              <button className="isd-btn-ghost" onClick={() => setShowCopyModal(false)} disabled={copyLoading}>Отмена</button>
              <button className="isd-btn-secondary" onClick={handleCopyConfirm} disabled={copyLoading || !copyName.trim()}>
                {copyLoading ? 'Копирование...' : 'Копировать'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InteractiveSceneDetail;
